import React, { useEffect, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { FiSend } from "react-icons/fi";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useToast } from "../../hooks/useToast";

interface ClonePassConfigurationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem | null;
  onCloned: (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => void;
}

export const ClonePassConfigurationDrawer = ({
  isOpen,
  onClose,
  businessId,
  passConfiguration,
  onCloned,
}: ClonePassConfigurationDrawerProps) => {
  const apiClient = useAdminApiClient();
  const { showSuccessToast, showErrorToast } = useToast();
  const [displayName, setDisplayName] = useState("");
  const [passDescription, setPassDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sourceId = passConfiguration?.Meta?.Id;
  const canSubmit =
    !!sourceId &&
    displayName.trim().length > 0 &&
    passDescription.trim().length > 0;

  useEffect(() => {
    if (!isOpen || !passConfiguration) {
      setDisplayName("");
      setPassDescription("");
      setIsSubmitting(false);
      return;
    }

    setDisplayName(`Copy of ${passConfiguration.DisplayName ?? ""}`.trim());
    setPassDescription("");
    setIsSubmitting(false);
  }, [isOpen, passConfiguration]);

  const onSubmit = async () => {
    if (!sourceId || !canSubmit) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await apiClient.clonePassConfiguration(businessId, {
        SourcePassConfigurationId: sourceId,
        DisplayName: displayName.trim(),
        PassDescription: passDescription.trim(),
      });

      if (!result.ok) {
        showErrorToast(result.message || "Failed to clone pass configuration.");
        return;
      }

      showSuccessToast("Pass configuration cloned successfully.");
      onClose();
      onCloned(result.data);
    } catch (error) {
      console.error("Clone pass configuration failed:", error);
      showErrorToast("An error occurred while cloning pass configuration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !passConfiguration) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="right"
      size="md"
      preserveScrollBarGap
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>CLONE PASS</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={6}>
            <FormControl isRequired>
              <FormLabel>Display Name</FormLabel>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Display name"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Pass Description</FormLabel>
              <Textarea
                value={passDescription}
                onChange={(e) => setPassDescription(e.target.value)}
                placeholder="Enter description"
                rows={4}
                fontSize="xs"
              />
            </FormControl>
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <Button
            colorScheme="cherryButton"
            borderRadius="full"
            leftIcon={<FiSend />}
            onClick={onSubmit}
            isLoading={isSubmitting}
            isDisabled={!canSubmit || isSubmitting}
          >
            Clone Configuration
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
